import { useEffect, useState } from 'react';

interface DebounceOptions<T> {
  /**
   * Values that take effect at once instead of waiting, such as an emptied
   * search box: there is nothing to wait for, and the debounced value has to
   * be reset there too, or typing again within the delay would hand back the
   * term that was just cleared.
   */
  immediate?: (value: T) => boolean;
}

/**
 * `value`, once it has stopped changing for `delayMs`.
 *
 * For inputs that drive a request: the field stays bound to the live value so
 * typing feels immediate, and only the settled value reaches the query key, so
 * "puma" is one request rather than four.
 */
export function useDebouncedValue<T>(
  value: T,
  delayMs: number,
  { immediate }: DebounceOptions<T> = {},
): T {
  const [debounced, setDebounced] = useState(value);
  const isImmediate = immediate?.(value) ?? false;

  // Set during render rather than in an effect, so the render that clears the
  // field is already the one that sees the reset — not the one after it.
  if (isImmediate && !Object.is(debounced, value)) {
    setDebounced(value);
  }

  useEffect(() => {
    if (isImmediate) return;
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs, isImmediate]);

  return isImmediate ? value : debounced;
}
